import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "~/lib/api-client";
import { useCanAuthorCustomAgents, useCurrentUser } from "~/hooks/queries/use-is-admin";
import { useCrons } from "~/hooks/queries/use-crons";
import { useWorkflowSchedules } from "~/hooks/queries/use-workflow-schedules";
import { useSkills } from "~/hooks/queries/use-skills";
import { useKnowledgeBases } from "~/hooks/queries/use-knowledge";
import { KNOWLEDGE_SERVICE_FLAG, useFeatureFlag } from "~/hooks/queries/use-feature-flags";
import { useAgentOverviewRows } from "~/features/agents/use-agent-overview-rows";
import {
  EMPTY_BUILD_ITEMS,
  toBuildItems,
  type BuildItem,
  type BuildKind,
  type BuildSources,
} from "~/components/command-palette-build-items";

type PaletteWorkflow = BuildSources["workflows"][number] & { updatedAt?: string };

/**
 * The two workflow scopes a person can run. The platform's `all` clamps back
 * to `mine` for non-admins, so one call would miss everything shared with a
 * member — the same reason `src/workflow/list-visible.ts` reads both.
 */
const WORKFLOW_SCOPES = ["mine", "shared"] as const;

/** Each id once, most recently updated first — `mergeVisibleWorkflows`' rule. */
export function mergeWorkflowScopes(
  pages: ReadonlyArray<ReadonlyArray<PaletteWorkflow> | undefined>,
): PaletteWorkflow[] {
  const byId = new Map<string, PaletteWorkflow>();
  for (const page of pages) {
    for (const row of page ?? []) if (!byId.has(row.id)) byId.set(row.id, row);
  }
  const at = (w: PaletteWorkflow) => {
    const t = Date.parse(w.updatedAt ?? "");
    return Number.isNaN(t) ? 0 : t;
  };
  return [...byId.values()].sort((a, b) => at(b) - at(a));
}

/**
 * One workflow scope through Eddy's proxy. A plain `api.get`, not the editor
 * library's list query: importing that library here would pull the whole
 * workflow editor into the always-loaded bundle, and the proxy authenticates
 * from the session cookie anyway (`src/workflow/ui-proxy.ts`).
 */
function useWorkflowScope(scope: (typeof WORKFLOW_SCOPES)[number], enabled: boolean) {
  return useQuery({
    queryKey: ["command-palette", "workflows", scope],
    queryFn: async () => {
      const res = await api.get<{ data?: PaletteWorkflow[] }>(
        `/api/platform/workflow/api/v1/workflows?scope=${scope}`,
      );
      return res.data ?? [];
    },
    enabled,
    retry: false,
  });
}

/**
 * FRONT-2436: the lists behind the palette's five build sections — the same
 * reads the Agents, Schedule, Skills and Knowledge pages make, with the same
 * cache keys, so a page visited earlier answers instantly.
 *
 * `enabled` is the palette's content-search gate: the palette is always
 * mounted, and must read none of this until someone actually searches.
 *
 * Each source fails on its own: a failed read leaves that one section empty.
 * Reads that keep the app's default retry hold `isLoading` until their retries
 * end; the workflow and schedule reads do not retry.
 */
export function usePaletteBuildSources(enabled: boolean): {
  items: Record<BuildKind, BuildItem[]>;
  isLoading: boolean;
} {
  const me = useCurrentUser();
  const { canAuthorCustomAgents } = useCanAuthorCustomAgents();
  // The palette shows no connect state, so it skips the providers read.
  const agents = useAgentOverviewRows(me, canAuthorCustomAgents, { enabled, withConnectability: false });
  const crons = useCrons(undefined, { enabled });
  const workflowSchedules = useWorkflowSchedules({ enabled });
  const skills = useSkills({ enabled });
  // The Knowledge bases tab's own gate: with the flag off the route 404s.
  const knowledgeEnabled = useFeatureFlag(KNOWLEDGE_SERVICE_FLAG, false);
  const knowledgeBases = useKnowledgeBases(enabled && knowledgeEnabled);
  const mineWorkflows = useWorkflowScope("mine", enabled);
  const sharedWorkflows = useWorkflowScope("shared", enabled);

  // Closed or idle, the palette builds nothing — even from lists a page cached.
  const items = useMemo(
    () =>
      !enabled
        ? EMPTY_BUILD_ITEMS
        : toBuildItems({
        knowledgeBases: knowledgeBases.data?.items ?? [],
        agents: agents.allRows,
        crons: crons.items,
        workflowSchedules: workflowSchedules.schedules,
        skills: skills.data ?? [],
        workflows: mergeWorkflowScopes([mineWorkflows.data, sharedWorkflows.data]),
      }),
    [
      enabled,
      knowledgeBases.data,
      agents.allRows,
      crons.items,
      workflowSchedules.schedules,
      skills.data,
      mineWorkflows.data,
      sharedWorkflows.data,
    ],
  );

  const anySourceLoading =
    agents.predefinedPending ||
      agents.customPending ||
      crons.isLoading ||
      workflowSchedules.isLoading ||
      skills.isLoading ||
      knowledgeBases.isLoading ||
      mineWorkflows.isLoading ||
      sharedWorkflows.isLoading;
  const isLoading = enabled && anySourceLoading;

  return { items, isLoading };
}
